import React, { useEffect, useState } from 'react';
import QuestionCard from './QuestionCard';
import { OptionPercentageStat } from './OptionPercentageStat';
import { isOrphanCard, ORPHAN_CARD_CLASSES } from './orphan-card';

export interface CustomOption {
  id: string;
  label: string;
  imageUrl?: string | null;
  popularityPct?: number;
  thirdImageUrl?: string | null;
}

interface CustomPickProps {
  questionId?: string;
  questionText: string;
  subtext?: string;
  /**
   * Optional question-level image rendered above the question text.
   * Used for image-identification questions (e.g. "Who is this coach?",
   * "What brand is this logo?") where the image IS the question prompt
   * and options are text-only to avoid telegraphing the answer.
   * See DKPLAT-363.
   */
  questionImageUrl?: string | null;
  options: CustomOption[];
  selectedOption?: string;
  onOptionSelect: (optionId: string | undefined) => void;
  points: number | string;
  earnedPoints?: number;
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

const CustomPick: React.FC<CustomPickProps> = ({
  questionId,
  questionText,
  subtext,
  questionImageUrl,
  options,
  selectedOption,
  onOptionSelect,
  points,
  earnedPoints,
  isCorrect,
  disabled = false,
  className = '',
  questionNumber,
  questionMetadata,
}) => {
  const isDailyDouble = questionMetadata?.dailyDouble || false;
  const hasResult = isCorrect !== undefined;

  // Track image load failures so the whole wrapper collapses on error —
  // hiding only the <img> via style.display would leave the mt/mb margins
  // as a blank gap above the question text. Reset on URL change so a
  // later swap (e.g. admin edit) gets a fresh attempt.
  const [imageErrored, setImageErrored] = useState(false);
  useEffect(() => {
    setImageErrored(false);
  }, [questionImageUrl]);

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : undefined}
      data-testtype="custom"
    >
      {questionImageUrl && !imageErrored && (
        <div className="flex justify-center mb-3 mt-2">
          <img
            src={questionImageUrl}
            alt=""
            data-testid="custom-question-image"
            className="max-h-32 object-contain"
            onError={() => setImageErrored(true)}
          />
        </div>
      )}

      {subtext && (
        <span
          data-testid="custom-subtext"
          className="text-[#ABABAB] font-light block text-center mt-2"
        >
          {subtext}
        </span>
      )}

      <span
        data-testid="custom-question"
        className="text-text-primary font-medium block text-center mb-4"
      >
        {questionText}
      </span>

      <div
        className={`grid gap-4 ${options.length > 4 ? 'grid-cols-1' : 'grid-cols-2'}`}
        data-testid="custom-selection"
      >
        {options.map((option, index) => {
          const isSelected = selectedOption === option.id;
          const resultBorderClass =
            isSelected && hasResult
              ? isCorrect
                ? 'border-2 border-[#8B5CF6] shadow-[0_0_15px_4px_rgba(139,92,246,0.4)]'
                : 'border-2 border-red-500 shadow-[0_0_15px_4px_rgba(239,68,68,0.4)]'
              : '';

          // The button is the grid item directly here (no wrapper div),
          // so the orphan classes go on the button and replace `w-full`.
          const isSingleColumn = options.length > 4;
          const isOrphan = isOrphanCard({ index, total: options.length, isSingleColumn });
          const orphanLayout = isOrphan ? ORPHAN_CARD_CLASSES : 'w-full';

          const getBackgroundClass = () => {
            if (isSelected) return 'bg-white';
            return isDailyDouble ? 'bg-[#000D1F]' : 'bg-[#0A0A0C]';
          };

          const getBorderClass = () => {
            if (hasResult) return resultBorderClass;
            if (isDailyDouble) {
              if (isSelected) return 'border border-white';
              return 'border border-[#0D54AD]';
            }
            if (isSelected) return 'border border-[#6F6F6F]';
            return 'border border-[#0A0A0C]';
          };

          const getHoverClass = () => {
            if (disabled || hasResult) return '';
            return isDailyDouble ? 'hover:border-white' : 'hover:border-[#6F6F6F]';
          };

          return (
            <button
              key={option.id}
              onClick={() => {
                if (!disabled) onOptionSelect(isSelected ? undefined : option.id);
              }}
              disabled={disabled}
              data-testid={`option-${option.id}`}
              data-testresult={
                isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined
              }
              data-selected={isSelected ? 'true' : 'false'}
              className={`
                group peer h-auto min-h-[140px] p-6 rounded-xl transition-smooth flex flex-col items-center justify-center relative
                ${orphanLayout}
                ${getBackgroundClass()}
                ${getBorderClass()}
                ${getHoverClass()}
                ${disabled && !hasResult ? 'cursor-not-allowed opacity-90' : ''}
              `}
            >
              {option.popularityPct !== undefined && options.length >= 2 && (
                <div className="absolute top-2 left-2">
                  <OptionPercentageStat
                    percentage={option.popularityPct}
                    isSelected={isSelected}
                  />
                </div>
              )}

              {option.thirdImageUrl && (
                <div className="absolute top-2 right-2 w-10 h-10">
                  <img
                    src={option.thirdImageUrl}
                    alt="Secondary"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              <div className="flex flex-col items-center space-y-3">
                {option.imageUrl && (
                  <img
                    src={option.imageUrl}
                    alt={option.label}
                    className="w-16 h-16 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div
                  className={`${
                    option.label.split(' ').some((l) => l.length > 14) ? 'text-md' : 'text-lg'
                  } min-h-12 ${isSelected ? 'text-black' : 'text-text-primary'}`}
                >
                  {option.label}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </QuestionCard>
  );
};

export default CustomPick;
